import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import VehicleCreateForm from '../molecules/VehicleCreateForm'
import { FETCH_MOTORBIKE, RESET_MOTORBIKE, UPDATE_MOTORBIKE } from 'src/redux/motorbikesReducer'
import { RootReducer } from 'src/redux/rootReducer'
import { MotorbikeFormData } from './MotorbikeCreate'

const MotorbikeEdit = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id } = useParams<{ id: string }>()

  const motorbike = useSelector((state: RootReducer) => {
    const { motorbike: current, motorbikeRequestId } = state.motorbikesReducer
    if (current === null || motorbikeRequestId !== id) {
      return null
    }
    return current
  })
  const motorbikeLoaded = useSelector((state: RootReducer) => state.motorbikesReducer.motorbikeLoaded)

  useEffect(() => {
    if (typeof id === 'string') {
      dispatch(FETCH_MOTORBIKE(id))
    }
    return () => {
      dispatch(RESET_MOTORBIKE())
    }
  }, [dispatch, id])

  const goBack = (): void => {
    if (typeof id === 'string' && id.length > 0) {
      navigate(`/selling/vehicles/${id}`)
      return
    }
    navigate('/selling/vehicles')
  }

  const onSubmit = (values: MotorbikeFormData): void => {
    dispatch(UPDATE_MOTORBIKE({ id: id ?? '', ...values }))
    goBack()
  }

  if (motorbike === null) {
    return (
      <Layout withoutToolbar>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">{motorbikeLoaded ? t('my_vehicles.empty_title') : '...'}</div>
        </div>
      </Layout>
    )
  }

  const initialValues: MotorbikeFormData = {
    model: motorbike.model,
    brand_id: motorbike.brand_id,
    vin: motorbike.vin ?? '',
    motorbike_name: motorbike.model,
    rails_app_id: motorbike.rails_app_id,
    rails_app_hash_id: motorbike.rails_app_hash_id,
    first_registration: motorbike.first_registration,
    total_mileage: motorbike.total_mileage?.toString() ?? null,
    engine_power: motorbike.engine_power?.toString() ?? null,
    number_of_previous_owners: motorbike.number_of_previous_owners?.toString() ?? null,
    capacity: motorbike.capacity?.toString() ?? null,
    metallic: motorbike.metallic ?? null,
    drive_type: motorbike.drive_type ?? null,
    gear_box: motorbike.gear_box ?? null,
    damage_unrepaired: motorbike.damage_unrepaired ?? null,
    abs: motorbike.abs ?? null,
    heated_grips: motorbike.heated_grips ?? null,
    box: motorbike.box ?? null,
    windshield: motorbike.windshield ?? null,
    next_hu: motorbike.next_hu ?? null,
    full_service_history: motorbike.full_service_history ?? null
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <div className="self-stretch h-14 min-h-14 bg-Estigroup-primary rounded-lg border border-neutral-200 flex flex-col justify-start items-center">
          <div className="self-stretch flex-1 px-4 py-3 inline-flex justify-start items-center">
            <div className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat">
              {t('my_vehicles.edit_vehicle')}
            </div>
          </div>
        </div>
        <VehicleCreateForm mode="edit" initialValues={initialValues} onSubmit={onSubmit} onCancel={goBack} />
      </div>
    </Layout>
  )
}

export default MotorbikeEdit
